import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from "@mui/material/Typography";
import Box from '@mui/material/Box';
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Skeleton from '@mui/material/Skeleton';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Dayjs } from 'dayjs';
import {
  GridColDef,
  GridCellParams
} from '@mui/x-data-grid';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useModal from '../../hooks/use-modal';
import { Filter } from '../../components/common/filter';
import { CustomDataGrid } from '../../components/common/datagrid';
import { projectStyle } from '../../styles/pages/project/project';

import { useQueryProject, useQueryCollaborationProjectReq, useMutationApproveReq, useMutationRejectReq, useMutationDeleteProject, useMutationUpdateProject } from '../../api/project';
import { ProjectList, CollaborationProjectReqList } from '../../types/project';
import { selectAuthUser } from '../../store/auth/selector';
import { useQueryClient } from '@tanstack/react-query';

interface ActionsProps {
  deleteParams: () => void;
  editParams: () => void;
  role: string;
  subrole: string;
}

function Actions({ deleteParams, editParams, role, subrole }: ActionsProps){
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disabled={((role === "Student" && subrole === "Collaborator") || (role === "Lecturer" && subrole !== "Author")) && true}
      >
      <MoreHorizIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={editParams}>
          <ListItemIcon sx={projectStyle.dataGridMenuMenuItemListItemIcon}>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={deleteParams}>
          <ListItemIcon sx={projectStyle.dataGridMenuMenuItemListItemIcon}>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
    </>
  );
}

const avatars = [
  { id: 1, imageUrl: '/assets/image/avatar.jpg' },
  { id: 2, imageUrl: '/assets/image/avatar.jpg' },
  { id: 3, imageUrl: '/assets/image/avatar.jpg' },
  { id: 4, imageUrl: '/assets/image/avatar.jpg' },
  { id: 5, imageUrl: '/assets/image/avatar.jpg' },
  { id: 6, imageUrl: '/assets/image/avatar.jpg' },
];

export function Project() {
  const { showModal } = useModal();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useSelector(selectAuthUser);
  
  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(null);
  const [EndDatevalue, setEndDateValue] = useState<Dayjs | null>(null);
  const [searchResult, setSearchResult] = useState('');
  const [projectListTotalItems, setProjectListTotalItems] = useState(0)
  const [projectListPaginationModel, setProjectListPaginationModel] = useState({pageSize: 10, page: 0});
  const [collaborationReqListTotalItems, setCollaborationReqListTotalItems] = useState(0)
  const [collaborationReqListPaginationModel, setCollaborationReqListPaginationModel] = useState({pageSize: 10, page: 0});
  const [selectedTab, setSelectedTab] = useState('1');
  const [projectList, setProjectList] = useState<ProjectList[]>([]);
  const [collaborationReqList, setCollaborationReqList] = useState<CollaborationProjectReqList[]>([]);

  const handleTabButtons = (event, newValue: string) => {
    if (newValue !== null) {
      setSelectedTab(newValue);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'projectName',
      headerName: 'Project Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <>
          <Box 
            component="img" 
            src={params.row.projectThumbnail}
            alt="img-proj"
            sx={projectStyle.dataGrid1Box1}
          />
          <Link tabIndex={params.tabIndex} color="black" underline="hover" onClick={() => navigate(`/project-detail/${params.row.projectId}`)}>{params.row.projectName}</Link>
        </>
      ),
    },
    {
      field: 'projectOwner',
      headerName: 'Project Owner',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'totalCollaborators',
      headerName: 'Collaborator',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={projectStyle.dataGrid1Box2}>
          <AvatarGroup spacing="small" max={3} sx={projectStyle.dataGrid1Box2AvatarGroup}>
            {params.row.collaborators?.map((item) => (
              <Avatar key={item.userId} alt={item.fullName} src={item.avatar} sx={projectStyle.dataGrid1Box2AvatarGroupAvatar}/>
            ))}
          </AvatarGroup>
          <Typography variant="caption">{params.row.totalCollaborators} Collaborators</Typography>
        </Box>
      )
    },
    {
      field: 'latestUpdate',
      headerName: 'Latest Update',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      align: 'center',
      renderCell: (params) => <Actions deleteParams={() => {deleteProjectConfirmation(params.row.projectId)}} editParams={() => {editProject(params.row.projectId)}} role={profile.role} subrole={params.row.userRole}/>
    },
  ];

  const columns2: GridColDef[] = [
    {
      field: 'projectName',
      headerName: 'Project Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridCellParams) => (
        <>
          <Box 
            component="img" 
            src={params.row.projectThumbnail}
            alt="img-coll"
            sx={projectStyle.dataGrid2Box1}
          />
          <Box>
            <Typography variant="body2">{params.row.projectName}</Typography>
          </Box>
        </>
      ),
    },
    {
      field: 'requestedBy',
      headerName: 'Request by',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'status',
      headerName: '',
      width: 250,
      align: 'center',
      renderCell: (params: GridCellParams) => (
        <Box>
          {params.row.status === 'Approved' && (
              <Chip label={params.row.status} sx={projectStyle.dataGrid2Box2Chip1}/>
          )}
          {params.row.status === 'Rejected' && (
              <Chip label={params.row.status} sx={projectStyle.dataGrid2Box2Chip2}/>
          )}
          {params.row.status === 'Waiting' && (
            <>
              <Button variant="contained" color="error" sx={projectStyle.dataGrid2Box2Button} onClick={() => rejectCollaborationConfirmation(params.row.requestId)}>Reject</Button>
              <Button variant="contained" color="success" onClick={() => approveCollaborationConfirmation(params.row.requestId)}>Approve</Button>
            </>
          )}
        </Box>
      )
    },
  ];

  const getProject = useQueryProject({
    query: {
      userId: profile.userId,
      search: searchResult,
      take: projectListPaginationModel.pageSize,
      page: projectListPaginationModel.page,
      startDate: startDateValue ? startDateValue?.format('YYYY-MM-DD') : "",
      endDate: EndDatevalue ? EndDatevalue?.format('YYYY-MM-DD') : ""
    },
    options: {
      onSuccess: (res) => {
        setProjectList(res.data.projects);
        setProjectListTotalItems(res.data.totalItems);
      },
      onError: () => {
        setProjectList([]);
      }
    },
  });

  const getCollaborationProjectReq = useQueryCollaborationProjectReq({
    query: {
      userId: profile.userId,
      search: searchResult,
      take: collaborationReqListPaginationModel.pageSize,
      page: collaborationReqListPaginationModel.page,
      startDate: startDateValue ? startDateValue?.format('YYYY-MM-DD') : "",
      endDate: EndDatevalue ? EndDatevalue?.format('YYYY-MM-DD') : ""
    },
    options: {
      onSuccess: (res) => {
        setCollaborationReqList(res.data.users);
        setCollaborationReqListTotalItems(res.data.totalItems);
      },
      onError: () => {
        setCollaborationReqList([]);
      }
    },
  });

  const editProject = (id: string) => {
    navigate(`/edit-project/${id}`)
  }

  const deleteProject = useMutationDeleteProject({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['project'] });
      }
    },
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Delete project successfully.',
        options: {
          variant: 'success',
        },
      });
    },
    onError: async (error) => {
      showModal({
        title: 'Error',
        message:
          'Delete project is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  })

  const deleteProjectConfirmation = (id: string) => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to delete this item?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          deleteProject.mutate({
            userId: profile.userId,
            projectId: id
          });
        },
      },
    });
  }

  const approveCollaboration = useMutationApproveReq({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['collaborationReq'] });
      }
    },
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Approve project collaboration successfully.',
        options: {
          variant: 'success',
        },
      });
    },
    onError: async (error) => {
      showModal({
        title: 'Error',
        message:
          'Approve project collaboration request is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  });

  const approveCollaborationConfirmation = (id: string) => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to approve this collaboration request?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          approveCollaboration.mutate({
            userId: profile.userId,
            requestId: id
          });
        },
      },
    });
  }

  const rejectCollaboration = useMutationRejectReq({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['collaborationReq'] });
      }
    },
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Reject project collaboration successfully',
        options: {
          variant: 'success',
        },
      });
    },
    onError: (error) => {
      showModal({
        title: 'Error',
        message:
          'Reject project collaboration request is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  });

  const rejectCollaborationConfirmation = (id: string) => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to reject this collaboration request?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          rejectCollaboration.mutate({
            userId: profile.userId,
            requestId: id
          });
        },
      },
    });
  }

  useEffect(() => {
    if(searchResult === null || searchResult === ""){
      getProject.refetch()
      getCollaborationProjectReq.refetch()
    }

  }, [searchResult])

  return (
    <Container maxWidth={false} sx={projectStyle.contentContainer}>
      <Grid container spacing={2}>
        <Grid xs={3}>
          <Typography variant="h3" sx={projectStyle.contentContainerGridItem1Typography}>Project</Typography>
        </Grid>
        <Grid container spacing={2} xs sx={projectStyle.contentContainerGrid}>
          <Filter 
            placeholder="Search Project" 
            onSearchChange={(searchTerm) => setSearchResult(searchTerm)} 
            onSearchClick={() => {
              getProject.refetch()
              getCollaborationProjectReq.refetch()
            }}
            startDate={(startDate) => setStartDateValue(startDate)}
            endDate={(endDate) => setEndDateValue(endDate)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={projectStyle.contentContainerGrid}>
        <TabContext value={selectedTab}>
          <Grid xs={12}>
            <Box sx={projectStyle.contentContainerGridItem2Box}>
              <Paper>
                <ToggleButtonGroup
                  size="small"
                  value={selectedTab}
                  exclusive
                  onChange={handleTabButtons}
                  aria-label="tab buttons"
                >
                  <ToggleButton value="1" >
                    Project
                  </ToggleButton>
                  <ToggleButton value="2">
                    Collaboration 
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>
              <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => navigate('/create-project')}>Create New Project</Button>
            </Box>
          </Grid>
          <Grid xs={12}>
            <TabPanel value="1" sx={projectStyle.contentContainerGridItem3TabPanel}>
              <Paper elevation={3}>
                {getProject.isLoading || getProject.isFetching ? 
                  (<Skeleton variant="rounded" width="auto" height="80vh" />) : (
                    <CustomDataGrid
                      columnsData={columns}
                      rowsData={projectList}
                      rowCount={projectListTotalItems}
                      getRowId={(row) => row.projectId}
                      setPaginationModel={(value) => {
                        setProjectListPaginationModel(value);
                      }} 
                      paginationModel={projectListPaginationModel}
                    />
                  )
                }
              </Paper>
            </TabPanel>
            <TabPanel value="2" sx={projectStyle.contentContainerGridItem3TabPanel}>
              <Paper elevation={3}>
                {getProject.isLoading || getProject.isFetching ? (
                  <Skeleton variant="rounded" width="auto" height="80vh" />
                ) : (
                  <CustomDataGrid
                    columnsData={columns2}
                    rowsData={collaborationReqList}
                    rowCount={collaborationReqListTotalItems}
                    getRowId={(row) => row.requestId}
                    setPaginationModel={(value) => {
                      setCollaborationReqListPaginationModel(value);
                    }} 
                    paginationModel={collaborationReqListPaginationModel}
                  />
                )}
              </Paper>
          </TabPanel>
          </Grid>
        </TabContext>
      </Grid>
    </Container>
  );
}